import { CONFIG } from '@h2h/shared';
import { Lock } from '../../../components/ui/icons';
import { signedPoints } from '../../../lib/format';
import { cn } from '../../../lib/cn';

const BASE = CONFIG.COMBO.BASE_POINTS;

/**
 * Unified session header — dark card so it reads as a summary layer above
 * the white fixture cards, giving the page clear visual hierarchy.
 *
 * Three states: upcoming / empty (no cards yet) / building (cards in progress).
 */
export type RoundHeaderProps =
  | { state: 'upcoming'; eyebrow: string; title: string; subtitle?: string; deadlineLabel?: string | null }
  | { state: 'empty'; deadlineLabel?: string | null }
  | {
      state: 'building';
      cardsBuilt: number;
      total: number;
      deadlineLabel?: string | null;
      mode: 'projected';
      toWin: number;
      atRisk: number;
    }
  | {
      state: 'building';
      cardsBuilt: number;
      total: number;
      deadlineLabel?: string | null;
      mode: 'live';
      banked: number;
      inPlay: number;
      remainingToWin: number;
      settledCount: number;
      liveCount: number;
    };

export function RoundHeader(props: RoundHeaderProps) {
  const deadlineLabel =
    props.state === 'upcoming' ? props.deadlineLabel
    : props.state === 'empty'   ? props.deadlineLabel
    :                             props.deadlineLabel;

  const accentGreen = props.state === 'building';

  const eyebrow =
    props.state === 'upcoming' ? props.eyebrow
    : props.state === 'building'
      ? `${props.cardsBuilt}${props.total > 0 ? ` / ${props.total}` : ''} card${props.cardsBuilt === 1 ? '' : 's'} built`
      : 'Build your cards';

  return (
    <div className="relative mt-3 overflow-hidden rounded-[18px] bg-[linear-gradient(135deg,#1C1D26,#111118)] shadow-[0_10px_26px_-10px_rgba(0,0,0,0.4)]">
      {/* subtle ambient glow */}
      <div className="pointer-events-none absolute -right-8 -top-10 h-[130px] w-[130px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.12),transparent_70%)]" />

      {/* ── Row 1: eyebrow + countdown ───────────────────────────────────────── */}
      <div className="relative flex items-center justify-between gap-3 px-[14px] pt-[11px]">
        <span className="truncate text-[10.5px] font-extrabold uppercase tracking-[0.09em] text-[#666672]">
          {eyebrow}
        </span>
        {deadlineLabel && <CountdownPill label={deadlineLabel} green={accentGreen} />}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="relative px-[14px] pb-[11px] pt-[6px]">
        {props.state === 'upcoming' && (
          <>
            <div className="text-[18px] font-extrabold leading-[1.2] tracking-[-0.02em] text-white">
              {props.title}
            </div>
            {props.subtitle && (
              <div className="mt-[3px] text-[11.5px] font-medium text-[#8A8A94]">{props.subtitle}</div>
            )}
          </>
        )}

        {props.state === 'empty' && (
          <>
            <div className="text-[17px] font-extrabold leading-[1.2] tracking-[-0.02em] text-white">
              Predict who wins, add boosts
            </div>
            <p className="mt-[4px] text-[11.5px] font-medium leading-[1.4] text-[#8A8A94]">
              Get the result right and you win{' '}
              <span className="font-bold text-[#2EDB7B]">{BASE} points</span> — each boost multiplies it. Get it wrong and you lose{' '}
              <span className="font-bold text-[#FF6B61]">{BASE}</span>.
            </p>
          </>
        )}

        {props.state === 'building' && props.mode === 'projected' && (
          <>
            <div className="flex items-stretch">
              <div className="flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#666672]">To win</div>
                <div className="num mt-[1px] text-[22px] font-extrabold leading-none tracking-[-0.04em] text-[#2EDB7B]">
                  {props.toWin}
                </div>
              </div>
              <div className="mx-[12px] w-px self-stretch bg-white/[0.1]" />
              <div className="flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#666672]">At risk</div>
                <div className="num mt-[1px] text-[22px] font-extrabold leading-none tracking-[-0.04em] text-[#FF6B61]">
                  {signedPoints(props.atRisk)}
                </div>
              </div>
            </div>

            {props.total > 0 && (
              <div className="mt-[9px] flex items-center gap-[8px]">
                <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/[0.1]">
                  <div
                    className="h-full rounded-full bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width] duration-300"
                    style={{ width: `${Math.max(3, (props.cardsBuilt / props.total) * 100)}%` }}
                  />
                </div>
                {props.cardsBuilt >= props.total && (
                  <span className="shrink-0 rounded-[6px] bg-[rgba(36,197,106,0.18)] px-[7px] py-[2px] text-[10px] font-extrabold text-[#2EDB7B]">
                    All done
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {props.state === 'building' && props.mode === 'live' && (
          <>
            <div className="flex items-stretch">
              <div className="flex-1">
                <div className="flex items-center gap-[5px]">
                  {props.liveCount > 0 && (
                    <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-[#2EDB7B]" />
                  )}
                  <div className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#666672]">
                    {props.liveCount > 0 ? 'Projected' : 'Scored'}
                  </div>
                </div>
                {(() => {
                  const headline = props.liveCount > 0 ? props.banked + props.inPlay : props.banked;
                  return (
                    <div
                      className={cn(
                        'num mt-[1px] text-[22px] font-extrabold leading-none tracking-[-0.04em]',
                        headline > 0 && 'text-[#2EDB7B]',
                        headline < 0 && 'text-[#FF6B61]',
                        headline === 0 && 'text-white/70'
                      )}
                    >
                      {signedPoints(headline)}
                    </div>
                  );
                })()}
                {props.liveCount > 0 && (
                  <div className="mt-[2px] text-[10px] font-semibold text-[#666672]">
                    <span className="text-white/70">{signedPoints(props.banked)}</span> banked ·{' '}
                    <span className="text-[#2EDB7B]">{signedPoints(props.inPlay)}</span> live
                  </div>
                )}
              </div>
              <div className="mx-[12px] w-px self-stretch bg-white/[0.1]" />
              <div className="flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.06em] text-[#666672]">Still to win</div>
                <div className="num mt-[1px] text-[22px] font-extrabold leading-none tracking-[-0.04em] text-[#2EDB7B]">
                  {props.remainingToWin}
                </div>
              </div>
            </div>

            {props.cardsBuilt > 0 && (
              <div className="mt-[9px] flex items-center gap-[8px]">
                <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/[0.1]">
                  <div
                    className="h-full rounded-full bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width] duration-300"
                    style={{
                      width: `${Math.max(3, (props.settledCount / Math.max(1, props.cardsBuilt)) * 100)}%`
                    }}
                  />
                </div>
                <span className="num shrink-0 text-[10px] font-extrabold text-[#666672]">
                  {props.liveCount > 0
                    ? `${props.liveCount} live`
                    : props.settledCount >= props.cardsBuilt
                      ? 'All settled'
                      : `${props.settledCount}/${props.cardsBuilt} settled`}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CountdownPill({ label, green }: { label: string; green: boolean }) {
  return (
    <div className="inline-flex shrink-0 items-center gap-[5px] rounded-full border border-white/[0.1] bg-white/[0.08] px-[9px] py-[5px]">
      <Lock width={10} height={10} strokeWidth={2.5} className={green ? 'text-[#3FE08B]' : 'text-[#F0B34A]'} />
      <span className={cn('num text-[11.5px] font-extrabold tracking-[-0.01em]', green ? 'text-[#3FE08B]' : 'text-[#F0B34A]')}>
        {label}
      </span>
    </div>
  );
}
